import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import TreatmentSection from '../components/TreatmentSection';
import { CheckCircle } from 'lucide-react';

const TreatmentsPage = () => {
  const treatmentProcessSteps = [
    {
      id: 1,
      title: 'Initial Consultation',
      description: 'Our experienced Ayurvedic physicians conduct a thorough assessment to understand your health condition and constitution (Prakriti).'
    },
    {
      id: 2,
      title: 'Customized Treatment Plan',
      description: 'Based on the diagnosis, we create a personalized treatment protocol that may include herbal medications, dietary guidelines, and therapeutic procedures.'
    },
    {
      id: 3,
      title: 'Treatment Implementation',
      description: "You'll receive specialized Ayurvedic therapies and medications under expert supervision at our facility."
    },
    {
      id: 4,
      title: 'Lifestyle Modification',
      description: 'We guide you on incorporating healthy habits, dietary changes, and yoga practices that complement your treatment.'
    },
    {
      id: 5,
      title: 'Follow-up Care',
      description: 'Regular follow-ups to monitor your progress and make necessary adjustments to your treatment plan for optimal results.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <div className="pt-16 md:pt-24 bg-swastik-lightBlue/10">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold text-swastik-blue font-serif mb-6">Our Treatments</h1>
          <div className="flex flex-wrap gap-2 mb-8">
            <Link to="/" className="text-swastik-blue hover:text-swastik-blue/70">Home</Link>
            <span className="text-gray-400">/</span>
            <span className="text-gray-600">Treatments</span>
          </div>
        </div>
      </div>
      
      <TreatmentSection />
      
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-swastik-green font-medium">Our Approach</span>
            <h2 className="section-title mx-auto after:left-1/2 after:-translate-x-1/2">How Our Treatment Process Works</h2>
            <p className="section-subtitle">
              We follow a structured approach to ensure that each patient receives personalized care tailored to their unique needs and health conditions.
            </p>
          </div>

          <div className="relative">
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-swastik-blue/20 -translate-x-1/2"></div>
            
            {treatmentProcessSteps.map((step, index) => (
              <div key={step.id} className="relative mb-12 last:mb-0">
                <div className={`md:w-1/2 ${index % 2 === 0 ? 'md:pr-12 md:ml-0' : 'md:pl-12 md:ml-auto'}`}>
                  <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
                    <div className="flex items-center mb-4">
                      <div className="bg-swastik-blue/10 text-swastik-blue font-bold rounded-full w-8 h-8 flex items-center justify-center mr-4">
                        {step.id}
                      </div>
                      <h3 className="text-xl font-bold text-swastik-blue">{step.title}</h3>
                    </div>
                    <p className="text-gray-600">{step.description}</p>
                  </div>
                </div>
                <div className="hidden md:block absolute left-1/2 top-6 -translate-x-1/2 w-6 h-6 rounded-full bg-swastik-blue"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-swastik-blue text-white">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold font-serif mb-4">Benefits of Ayurvedic Treatments</h2>
            <div className="h-1 w-20 bg-swastik-earth mx-auto mb-8"></div>
            <p className="text-lg md:text-xl text-white/80">
              Ayurveda offers a holistic approach to health that addresses the root cause of disease rather than just treating symptoms.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="flex items-start bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <CheckCircle className="h-6 w-6 text-swastik-earth mr-4 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-xl font-bold mb-2">Natural Healing</h3>
                <p className="text-white/80">Our treatments use only natural herbs and therapies, eliminating the risk of side effects common with conventional medicine.</p>
              </div>
            </div>
            
            <div className="flex items-start bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <CheckCircle className="h-6 w-6 text-swastik-earth mr-4 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-xl font-bold mb-2">Long-lasting Results</h3>
                <p className="text-white/80">By addressing the root cause of diseases, our treatments offer sustainable improvement rather than temporary relief.</p>
              </div>
            </div>
            
            <div className="flex items-start bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <CheckCircle className="h-6 w-6 text-swastik-earth mr-4 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-xl font-bold mb-2">Holistic Wellness</h3>
                <p className="text-white/80">We focus on overall well-being, improving not just the targeted condition but your entire health profile.</p>
              </div>
            </div>
            
            <div className="flex items-start bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <CheckCircle className="h-6 w-6 text-swastik-earth mr-4 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-xl font-bold mb-2">Personalized Care</h3>
                <p className="text-white/80">Each treatment plan is customized according to your unique body constitution and health needs.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default TreatmentsPage;
